import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/shared/lib/css";
import { getNotificationRoute } from "@/shared/model/notification-routes";

import type { Notification } from "../model/use-notifications-model";

// ============================================================================
// Types
// ============================================================================

type NotificationDetailProps = {
  notification: Notification;
  className?: string;
};

// ============================================================================
// Helpers
// ============================================================================

function getNotifiableType(
  notifiable: Notification["notifiable"],
): string | null {
  if (!notifiable) return null;

  // Check for explicit type field
  if ("type" in notifiable && typeof notifiable.type === "string") {
    return notifiable.type;
  }

  // Infer type from discriminated union properties
  if ("purchase_id" in notifiable && !("store_id" in notifiable))
    return "Purchase";
  if ("store_id" in notifiable && !("purchase_id" in notifiable)) return "Sale";
  if ("reason" in notifiable) return "Dispute";
  if ("rating" in notifiable) return "Review";
  if ("amount_cents" in notifiable && "status" in notifiable)
    return "Withdrawal";

  return null;
}

// ============================================================================
// Component
// ============================================================================

export function NotificationDetail({
  notification,
  className,
}: NotificationDetailProps) {
  const navigate = useNavigate();

  // Get notifiable type and id
  const notifiableType = getNotifiableType(notification.notifiable);
  const notifiableId = notification.notifiable?.id ?? null;

  // Get route using shared helper
  const route = getNotificationRoute(notifiableType, notifiableId);

  return (
    <div className={cn("p-4 space-y-4", className)}>
      {/* Message with proper newline and emoji rendering */}
      <div className="text-sm leading-relaxed">
        <p className="whitespace-pre-line break-words">
          {notification.message}
        </p>
      </div>

      {/* Open button if route available */}
      {route && (
        <div className="pt-3 border-t">
          <button
            onClick={() => navigate(route)}
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ExternalLink className="size-4" />
            Открыть
          </button>
        </div>
      )}
    </div>
  );
}
